/* Profile / Account — mock screen reached from the sidebar user chip. */

function Switch({ on, onToggle }) {
  return (
    <button className={'switch' + (on ? ' on' : '')} onClick={onToggle} role="switch" aria-checked={on}>
      <span className="knob"></span>
    </button>
  );
}

const PROFILE_ACCENTS = ['var(--grad-warm)', '#6334E3', '#2A6FDB', '#1F8A5B', '#C8553D'];

function EditProfileModal({ profile, onSave, onClose }) {
  const [draft, setDraft] = React.useState(profile);
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const initials = (draft.fullName.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('') || '·').toUpperCase();
  const ink = draft.accent === 'var(--grad-warm)' ? 'var(--ink-on-brand)' : '#fff';
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.email.trim());
  const canSave = draft.fullName.trim() && emailOk;

  return ReactDOM.createPortal(
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="fb-head">
          <div>
            <div className="row gap-8" style={{ marginBottom: 7 }}>
              <span className="chip" style={{ background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)', fontWeight: 700 }}>Edit profile</span>
            </div>
            <h2 className="fb-title">Your details</h2>
            <p className="fb-sub">How you show up across the squadron.</p>
          </div>
          <button className="icon-act" onClick={onClose} title="Close"><I.x size={16} /></button>
        </div>

        <div className="fb-body">
          <div className="ep-avatar-row">
            <div className="avatar" style={{ width: 64, height: 64, fontSize: 23, borderRadius: 18, background: draft.accent, color: ink, flexShrink: 0 }}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <label className="field-lbl mb-0">Avatar accent</label>
              <div className="row gap-8" style={{ marginTop: 8 }}>
                {PROFILE_ACCENTS.map(a => (
                  <button key={a} type="button" className={'ep-swatch' + (draft.accent === a ? ' on' : '')} style={{ background: a }} onClick={() => set('accent', a)} aria-label="accent">
                    {draft.accent === a && <I.check size={13} style={{ color: a === 'var(--grad-warm)' ? 'var(--ink-on-brand)' : '#fff' }} />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="ep-field"><label className="field-lbl">Full name</label><input className="input" value={draft.fullName} onChange={e => set('fullName', e.target.value)} placeholder="Your name" /></div>
          <div className="ep-field"><label className="field-lbl">Callsign</label><input className="input" value={draft.callsign} onChange={e => set('callsign', e.target.value)} placeholder="Maverick" /></div>
          <div className="ep-field">
            <label className="field-lbl">Work email</label>
            <input className="input" type="email" value={draft.email} onChange={e => set('email', e.target.value)} placeholder="you@mavlers.com" />
            {!emailOk && draft.email.trim() && <span className="ep-err">Enter a valid email address.</span>}
          </div>
          <p className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Role, workspace and seat allocation are managed by your admin.</p>
        </div>

        <div className="fb-foot">
          <span className="muted" style={{ fontSize: 11.5 }}>Visible to your workspace</span>
          <div className="row gap-10">
            <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-lg" onClick={() => onSave(draft)} disabled={!canSave} title={canSave ? 'Save changes' : 'Name and a valid email are required'}><I.check size={15} /> Save changes</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* AUTH-02 Security card. The server verifies the current password, sets the new hash,
   revokes every OTHER session and re-issues this one — so the page stays signed in. */
function SecurityCard() {
  const [cur, setCur] = React.useState('');
  const [next, setNext] = React.useState('');
  const [again, setAgain] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);      // {ok, text}
  const valid = cur.length > 0 && next.length >= 10 && next === again && next !== cur;
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setMsg(null);
    try {
      await window.GooseAPI.changePassword(cur, next);
      setCur(''); setNext(''); setAgain('');
      setMsg({ ok: true, text: 'Password changed. Every other device has been signed out.' });
    } catch (e) { setMsg({ ok: false, text: (e && e.data && e.data.error) || e.message || 'Could not change the password.' }); }
    setBusy(false);
  };
  const signOutAll = async () => {
    if (busy) return;
    setBusy(true);
    try { const result = await window.GooseSignOut(true); if (result.ok) window.location.reload(); }
    catch (e) { setMsg({ ok: false, text: 'Could not sign out everywhere.' }); setBusy(false); }
  };
  return (
    <div className="card card-pad">
      <div className="sec-head"><span className="sec-title">Security</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div className="ep-field"><label className="field-lbl">Current password</label>
          <input className="input" type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
        <div className="ep-field"><label className="field-lbl">New password</label>
          <input className="input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="at least 10 characters" /></div>
        <div className="ep-field"><label className="field-lbl">Repeat new password</label>
          <input className="input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} /></div>
      </div>
      {next && next.length < 10 && <p className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>At least 10 characters.</p>}
      {again && next !== again && <p className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>The new passwords don't match.</p>}
      {msg && <p style={{ fontSize: 12.5, marginTop: 8, color: msg.ok ? 'var(--pass)' : 'var(--fail)' }} role="alert">{msg.text}</p>}
      <div className="row between wrap" style={{ marginTop: 12, gap: 10 }}>
        <button className="btn btn-quiet" disabled={busy} onClick={signOutAll} title="Revoke every session for this account, including this one">Sign out everywhere</button>
        <button className="btn btn-primary" disabled={!valid || busy} onClick={submit}>{busy ? <span className="spinner" /> : null} Change password</button>
      </div>
    </div>
  );
}

function Profile({ go }) {
  // Resolve the CURRENT signed-in user's live team row. Was hardcoded to the
  // pre-rename 'jd' member (now gone) → me={} → every tile fell back to mock
  // ($160 credits = 500−340, 3/5 afterburners). Match the live row by name.
  const cu = window.MAV.currentUser;
  const me = (cu && (window.MAV.team || []).find((m) => m.name === cu.name))
    || (window.MAV.memberById && window.MAV.memberById('as'))
    || {};
  const summary = window.MAV.summary || {};
  const abLeft = me.afterburnersLeft ?? 3, abCap = me.afterburnersCap ?? 5;
  // Per-user credit budget = your spend limit minus what you've spent (live).
  const creditsLeft = Math.round((me.limit ?? 500) - (me.used ?? 340));
  // Profile vanity stats, now live: your build count + the workspace pass rate
  // (single-operator here, so it IS your rate). Fall back to the old mock offline.
  const buildsShipped = me.builds || (summary.total_builds != null ? Number(summary.total_builds) : 128);
  const verdicted = summary.verdicted != null ? Number(summary.verdicted) : 0;
  const passRate = (summary.passed != null && verdicted > 0) ? Math.round(Number(summary.passed) / verdicted * 100) : 91;
  const refreshOn = me.refreshOn ?? 'Monday, Jun 1';
  const [profile, setProfile] = React.useState({
    fullName: (cu && cu.name) || 'Aum Shukla', callsign: 'Maverick',
    email: (cu && cu.email) || 'aum@mavlers.com', accent: 'var(--grad-warm)',
  });
  const [editing, setEditing] = React.useState(false);
  const [theme, setTheme] = React.useState(() => window.GooseTheme.get());
  React.useEffect(() => {
    const h = (e) => setTheme(e.detail);
    window.addEventListener('goose-theme-change', h);
    return () => window.removeEventListener('goose-theme-change', h);
  }, []);
  const initials = (profile.fullName.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('') || 'JD').toUpperCase();
  const avatarInk = profile.accent === 'var(--grad-warm)' ? 'var(--ink-on-brand)' : '#fff';
  const saveProfile = (next) => {
    setProfile(next);
    // Sync the LIVE signed-in user's shared roster record (was the mock 'jd', now
    // the resolved `me`) so other screens see the renamed user.
    if (me && me.id) me.name = next.fullName;
    setEditing(false);
  };
  const [prefs, setPrefs] = React.useState({
    builds: true, review: true, weekly: false, budget: true,
  });
  const flip = (k) => setPrefs(p => ({ ...p, [k]: !p[k] }));

  const sessions = [
    { dev: 'MacBook Pro · Chrome', loc: 'Pune, IN', when: 'Active now', cur: true },
    { dev: 'iPhone 15 · Safari', loc: 'Pune, IN', when: '2 hours ago' },
    { dev: 'Windows · Edge', loc: 'Ahmedabad, IN', when: 'Yesterday' },
  ];

  const Field = ({ label, value, sub }) => (
    <div className="acct-field">
      <label className="field-lbl mb-0">{label}</label>
      <div className="input" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{value}</span>
        {sub && <span className="chip mono" style={{ fontSize: 10.5 }}>{sub}</span>}
      </div>
    </div>
  );

  return (
    <div className="page fade-in">
      <div className="page-head">
        <div><h1 className="page-title">Account</h1><p className="page-desc">Your profile, notification preferences, and security.</p></div>
        <div className="acct-head-right">
          <div className="acct-meters">
            <div className="acct-meter" title="Afterburner QA runs available this week">
              <span className="acct-meter-ico ab"><I.flame size={15} /></span>
              <div>
                <div className="acct-meter-v">{abLeft}<small> / {abCap}</small></div>
                <div className="acct-meter-k">Afterburners left</div>
              </div>
            </div>
            <div className="acct-meter" title="Build credits remaining this cycle">
              <span className="acct-meter-ico cr"><I.bolt size={15} /></span>
              <div>
                <div className="acct-meter-v" data-acct-credits>${creditsLeft}</div>
                <div className="acct-meter-k">Credits left</div>
              </div>
            </div>
            <button className="btn btn-ghost" onClick={() => setEditing(true)}><I.settings size={15} /> Edit profile</button>
          </div>
          <div className="acct-refresh"><I.retry size={12} /> Refreshes {refreshOn}</div>
        </div>
      </div>

      {/* Identity header */}
      <div className="card card-pad" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <div className="avatar" title={'Callsign: ' + profile.callsign} style={{ width: 66, height: 66, fontSize: 24, borderRadius: 20, background: profile.accent, color: avatarInk }}>{initials}</div>
        <div className="grow" style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{profile.fullName}</div>
          <div className="row gap-8 mt-6 wrap">
            <span className="chip" style={{ background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)', fontWeight: 700 }}>Producer</span>
            <span className="chip"><I.building size={11} style={{ verticalAlign: '-1px', marginRight: 3 }} />{(window.MAV.memberPath(me.id) || {}).str || 'Mavlers workspace'}</span>
            <span className="muted mono" style={{ fontSize: 12, alignSelf: 'center' }}>{profile.email}</span>
          </div>
        </div>
        <div className="row gap-16" style={{ alignSelf: 'stretch', alignItems: 'center' }}>
          <div className="col" style={{ alignItems: 'flex-end' }}><span className="page-title" style={{ fontSize: 22 }} data-acct-builds>{buildsShipped}</span><span className="muted" style={{ fontSize: 11 }}>builds shipped</span></div>
          <div style={{ width: 1, background: 'var(--hairline)', alignSelf: 'stretch' }}></div>
          <div className="col" style={{ alignItems: 'flex-end' }}><span className="page-title" style={{ fontSize: 22, color: passRate >= 75 ? 'var(--pass)' : passRate >= 40 ? 'var(--cond)' : 'var(--fail)' }} data-acct-pass>{passRate}%</span><span className="muted" style={{ fontSize: 11 }}>your pass rate</span></div>
        </div>
      </div>

      <div className="grid-2-1">
        <div className="col gap-20">
          {/* Account details */}
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title">Account details</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Full name" value={profile.fullName} />
              <Field label="Callsign" value={profile.callsign} sub="Goose has your six" />
              <Field label="Work email" value={profile.email} sub="Verified" />
              <Field label="Role" value="Producer" sub="Admin can change" />
              <Field label="Workspace" value="Mavlers" />
              <Field label="Member since" value="Aug 2024" />
            </div>
          </div>

          {/* Security — AUTH-02: self-service password rotation + sign out everywhere */}
          <SecurityCard />

          {/* Notifications */}
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title">Notifications</span></div>
            {[
              ['builds', 'Build completed', 'When one of your builds reaches PASS or FAIL'],
              ['review', 'Needs your review', 'A build is blocked waiting on your decision'],
              ['budget', 'Budget warnings', 'When spend crosses the soft threshold'],
              ['weekly', 'Weekly summary', 'A Monday digest of throughput and spend'],
            ].map(([k, t, d]) => (
              <div key={k} className="pref-row">
                <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>{t}</div><div className="muted" style={{ fontSize: 12 }}>{d}</div></div>
                <Switch on={prefs[k]} onToggle={() => flip(k)} />
              </div>
            ))}
          </div>

          {/* Appearance */}
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title">Appearance</span></div>
            <div className="seg" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              {[
                ['light', 'Warm light', 'The default Goose theme'],
                ['dark', 'OLED dark', 'True-black, high contrast'],
                ['console', 'Dark console', 'For long mission-control sessions'],
              ].map(([id, t, d]) => (
                <button key={id} className={'seg-opt' + (theme === id ? ' sel' : '')} onClick={() => window.GooseTheme.set(id)}>
                  <div className="sl">{t}</div><div className="sd">{d}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="col gap-20">
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title">Security</span></div>
            <div className="pref-row">
              <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>Password</div><div className="muted" style={{ fontSize: 12 }}>Last changed 3 months ago</div></div>
              <button className="btn btn-ghost" style={{ padding: '7px 13px', fontSize: 12.5 }}>Change</button>
            </div>
            <div className="pref-row">
              <div style={{ minWidth: 0 }}><div style={{ fontSize: 13.5, fontWeight: 600 }}>Two-factor</div><div className="muted" style={{ fontSize: 12 }}>Authenticator app</div></div>
              <span className="pill pill-pass" style={{ flexShrink: 0 }}><span className="dot"></span>On</span>
            </div>
            <div className="pref-row" style={{ borderBottom: 'none' }}>
              <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>OpenRouter key</div><div className="muted mono" style={{ fontSize: 12 }}>sk-or-•••• 4f2a</div></div>
              <button className="btn btn-ghost" style={{ padding: '7px 13px', fontSize: 12.5 }}>Rotate</button>
            </div>
          </div>

          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title">Active sessions</span></div>
            {sessions.map((s, i) => (
              <div key={i} className="sess-row">
                <span className="sess-ico"><I.layers size={16} /></span>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{s.dev}</div>
                  <div className="muted" style={{ fontSize: 11 }}>{s.loc} · {s.when}</div>
                </div>
                {s.cur ? <span className="pill pill-pass" style={{ fontSize: 10.5 }}><span className="dot"></span>This device</span>
                       : <button className="btn btn-quiet" style={{ padding: '4px 8px', fontSize: 11.5 }}>Revoke</button>}
              </div>
            ))}
          </div>

          <button className="btn btn-danger btn-block" style={{ justifyContent: 'center' }} onClick={() => window.GooseSignOut().then(result => { if (result.ok) go('login'); }).catch(() => {})}>
            <I.cancel size={15} /> Sign out
          </button>
        </div>
      </div>

      {editing && <EditProfileModal profile={profile} onSave={saveProfile} onClose={() => setEditing(false)} />}
    </div>
  );
}
window.Profile = Profile;
